import { FC, useDeferredValue, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom'

import { ArrowBack, Search } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'

import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { useGetInternalFeatureMapQuery } from '../../api/features'
import { useGetInternalGameDocumentQuery } from '../../api/games'
import { useGetInternalMarketsQuery } from '../../api/internal-markets'
import { useGetInternalGameScreenshotsQuery } from '../../api/screenshot-links'
import { ImplementationScreenshotCard } from '../../features/implementation-examples/components/ImplementationScreenshotCard/ImplementationScreenshotCard'
import { countMarketScreenshots, FeatureSection, getFeatureSections } from '../../features/implementation-examples/helpers/implementationExampleHelpers'
import { defaultImplementationMarketIso, getImplementationExamplesPath } from '../../features/implementation-examples/helpers/implementationExamplePaths'
import { ReviewGameHeader } from '../../features/review/components/ReviewGameHeader/ReviewGameHeader'

const initialScreenshotsPerFeature = 8
const moreScreenshotsPerFeature = 24

const FeatureScreenshots: FC<{ section: FeatureSection; gameId: string; marketIso: string }> = ({ section, gameId, marketIso }) => {
  const { t } = useTranslation()
  const [shownCount, setShownCount] = useState(initialScreenshotsPerFeature)
  const total = section.screenshots.length
  return (
    <Box component="section" aria-label={section.feature.name}>
      <Typography variant="h6" mb={1}>
        {t('internal-analysts:implementation_feature_heading', { name: section.feature.name, count: total })}
      </Typography>
      <Grid container spacing={2}>
        {section.screenshots.slice(0, shownCount).map(({ screenshot, choice }) => (
          <Grid item key={screenshot.id} xs={12} sm={6} md={4} lg={3}>
            <ImplementationScreenshotCard screenshot={screenshot} choice={choice} gameId={gameId} marketIso={marketIso} />
          </Grid>
        ))}
      </Grid>
      {shownCount < total && (
        <Button sx={{ mt: 1 }} onClick={() => setShownCount((count) => count + moreScreenshotsPerFeature)}>
          {t('internal-analysts:implementation_show_more', { shown: shownCount, total })}
        </Button>
      )}
    </Box>
  )
}

export const ImplementationScreenshotsPage: FC = () => {
  const { gameId = '' } = useParams<{ gameId: string }>()
  usePage(pageService.getInternalPageWithId('internal-implementation-screenshots'))
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const marketIso = searchParams.get('marketIso') || defaultImplementationMarketIso
  const featureSearch = searchParams.get('feature') ?? ''
  const commentedOnly = searchParams.get('commented') === '1'
  const deferredFeatureSearch = useDeferredValue(featureSearch)
  const gameQuery = useGetInternalGameDocumentQuery(gameId, { skip: !gameId })
  const screenshotsQuery = useGetInternalGameScreenshotsQuery(gameId, { skip: !gameId, refetchOnMountOrArgChange: true })
  const featureMapQuery = useGetInternalFeatureMapQuery({ marketIso })
  const marketsQuery = useGetInternalMarketsQuery()

  const setParam = (key: string, value: string, defaultValue: string) =>
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params)
        if (value && value !== defaultValue) next.set(key, value)
        else next.delete(key)
        return next
      },
      { replace: true }
    )

  const markets = useMemo(() => [...(marketsQuery.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)), [marketsQuery.data])
  const marketName = markets.find(({ iso }) => iso === marketIso)?.name ?? marketIso
  const screenshots = useMemo(() => screenshotsQuery.data ?? [], [screenshotsQuery.data])
  const counts = useMemo(() => countMarketScreenshots(screenshots, marketIso), [screenshots, marketIso])
  const sections = useMemo(
    () =>
      featureMapQuery.data
        ? getFeatureSections({ screenshots, featureMap: featureMapQuery.data, marketIso, featureSearch: deferredFeatureSearch, commentedOnly })
        : [],
    [commentedOnly, deferredFeatureSearch, featureMapQuery.data, marketIso, screenshots]
  )

  const isLoading = gameQuery.isLoading || screenshotsQuery.isLoading || featureMapQuery.isLoading
  const loadError = gameQuery.isError
    ? t('internal-analysts:review_game_load_failed')
    : screenshotsQuery.isError
    ? t('internal-analysts:implementation_screenshots_load_failed')
    : featureMapQuery.isError
    ? t('internal-analysts:implementation_features_load_failed')
    : undefined

  const renderSections = () => {
    if (isLoading) return <Skeleton variant="rectangular" height={360} />
    if (loadError) return <Typography color="error.main">{loadError}</Typography>
    if (!sections.length)
      return (
        <Typography color="text.secondary">
          {t(counts.screenshots ? 'internal-analysts:implementation_no_matching_screenshots' : 'internal-analysts:implementation_no_screenshots', {
            market: marketName,
          })}
        </Typography>
      )
    return (
      <Stack spacing={4}>
        {sections.map((section) => (
          <FeatureScreenshots key={`${marketIso}-${section.feature.legacyId}`} section={section} gameId={gameId} marketIso={marketIso} />
        ))}
      </Stack>
    )
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button startIcon={<ArrowBack />} component={RouterLink} to={getImplementationExamplesPath(marketIso)}>
            {t('internal-common:back')}
          </Button>
        </Stack>
        <TextField
          select
          size="small"
          label={t('internal-common:market')}
          value={marketIso}
          onChange={(event) => setParam('marketIso', event.target.value, defaultImplementationMarketIso)}
          sx={{ minWidth: 220 }}
        >
          {markets.map((market) => (
            <MenuItem key={market.id} value={market.iso}>
              {market.name}
            </MenuItem>
          ))}
          {!markets.some(({ iso }) => iso === marketIso) && <MenuItem value={marketIso}>{marketIso}</MenuItem>}
        </TextField>
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }}>
            {gameQuery.data ? (
              <ReviewGameHeader
                game={gameQuery.data}
                detail={
                  screenshotsQuery.data
                    ? t('internal-analysts:implementation_screenshots_summary', { market: marketName, count: counts.screenshots, comments: counts.comments })
                    : undefined
                }
              />
            ) : (
              <Skeleton variant="rectangular" width={240} height={48} />
            )}
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                size="small"
                value={featureSearch}
                onChange={(event) => setParam('feature', event.target.value, '')}
                placeholder={t('internal-analysts:implementation_feature_search')}
                inputProps={{ 'aria-label': t('internal-analysts:implementation_feature_search') }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 260 }}
              />
              <FormControlLabel
                control={<Checkbox checked={commentedOnly} onChange={(event) => setParam('commented', event.target.checked ? '1' : '', '')} />}
                label={t('internal-analysts:implementation_commented_only')}
                sx={{ flexShrink: 0 }}
              />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {renderSections()}
    </Box>
  )
}
